import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { CalendarClock, Pencil, Plus } from 'lucide-react'
import { type ReactNode } from 'react'

import {
  Badge,
  Button,
  EmptyState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import { fetchFollowupRules } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * CRM → Regras de follow-up (F053): "comprou ar, lembrar da manutenção em 6
 * meses" — a palavra do dono.
 *
 * Uma regra diz: quando alguém comprar isto (um produto, ou tudo de uma
 * marca), fale com ele daqui a tanto tempo, com este modelo aprovado. A
 * materialização (a venda concluída virando tarefa) e o despacho (o cron)
 * acontecem no worker — esta tela só administra a REGRA e mostra quantas
 * tarefas cada uma tem pendentes agora.
 *
 * **O Aeris não tem uma tabela de categoria de produto.** "Tudo de uma
 * marca" casa por `product.brand` — o único agrupamento que já existe (ver
 * o comentário no topo da migração 0038).
 *
 * Esta tela é a LISTA. Criar e editar viraram páginas (`/crm-regras/nova` e
 * `/crm-regras/$id`, F065) quando a regra passou a aceitar VÁRIOS produtos:
 * uma lista que cresce dentro de um diálogo de altura fixa empurra os botões
 * para o meio da caixa — o defeito concreto que as filas (F058) já tinham.
 */

const dateLabel = (iso: string | null): string => {
  if (iso === null) return '—'
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const CrmRegrasPage = (): ReactNode => {
  const { allows } = useAuth()
  const canManage = allows('communication.followup.manage')

  const rules = useQuery({
    queryKey: ['communication-followup-rules'],
    queryFn: async ({ signal }) => await fetchFollowupRules(signal),
  })

  const rows = rules.data ?? []

  return (
    <>
      <TabHeader
        title="Regras de follow-up"
        description='"Comprou ar-condicionado? Sabe que em 6 meses precisa de manutenção" — uma regra por produto (ou marca), com prazo e modelo aprovado.'
        actions={
          canManage ? (
            <Button asChild>
              <Link to="/crm-regras/nova">
                <Plus aria-hidden="true" /> Nova regra
              </Link>
            </Button>
          ) : null
        }
      />

      {rules.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : rules.isError ? (
        <EmptyState
          icon={<CalendarClock aria-hidden="true" className="h-8 w-8" />}
          title="Não foi possível carregar as regras"
          description="Verifique a conexão com o servidor e tente novamente."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<CalendarClock aria-hidden="true" className="h-8 w-8" />}
          title="Nenhuma regra de follow-up"
          description={
            canManage
              ? 'Crie uma regra para lembrar o cliente automaticamente depois de uma venda.'
              : 'Peça a um administrador para cadastrar uma regra de follow-up.'
          }
        />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Regra</TableHeaderCell>
                <TableHeaderCell>Dispara em</TableHeaderCell>
                <TableHeaderCell>Prazo</TableHeaderCell>
                <TableHeaderCell>Pendentes</TableHeaderCell>
                <TableHeaderCell>Próximo vencimento</TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                {canManage ? <TableHeaderCell>&nbsp;</TableHeaderCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>{rule.name}</TableCell>
                  <TableCell>
                    {rule.triggerKind === 'product_purchased'
                      ? 'Produto'
                      : `Marca: ${rule.productBrand ?? '—'}`}
                  </TableCell>
                  <TableCell>
                    {[
                      rule.delayMonths > 0
                        ? `${String(rule.delayMonths)} ${rule.delayMonths === 1 ? 'mês' : 'meses'}`
                        : null,
                      rule.delayDays > 0
                        ? `${String(rule.delayDays)} ${rule.delayDays === 1 ? 'dia' : 'dias'}`
                        : null,
                    ]
                      .filter((part) => part !== null)
                      .join(' e ') || 'No mesmo dia'}
                  </TableCell>
                  <TableCell>{rule.pendingTaskCount}</TableCell>
                  <TableCell>{dateLabel(rule.nextDueAt)}</TableCell>
                  <TableCell>
                    <Badge tone={rule.enabled ? 'success' : 'neutral'}>
                      {rule.enabled ? 'Ligada' : 'Desligada'}
                    </Badge>
                  </TableCell>
                  {canManage ? (
                    <TableCell>
                      <Button asChild variant="ghost">
                        <Link
                          to="/crm-regras/$id"
                          params={{ id: rule.id }}
                          aria-label={`Editar ${rule.name}`}
                        >
                          <Pencil aria-hidden="true" className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  )
}
